import * as React from "react";
import { Box, CPHrBreak, CPText } from "shared/components/base";
import CPButton from "shared/components/shared/CPButton/CPButton";
import CPActionsheetModal from "shared/components/shared/CPActionsheetModal/CPActionsheetModal";
import CPResourceSecurityHelpModal from "shared/components/app/CPResourceSecurityHelpModal/CPResourceSecurityHelpModal";
import FileStorageConfigHelpModal from "./FileStorageConfigHelpModal";
import CPHelpButton from "shared/components/shared/CPHelpButton/CPHelpButton";
import CPTextBox from "shared/components/shared/CPTextBox/CPTextBox";
import CPTextArea from "shared/components/shared/CPTextArea/CPTextArea";
import CPCheckbox from "shared/components/shared/CPCheckbox";
import CPDropDownList from "shared/components/shared/CPDropDownList/CPDropDownList";
import useAppState from "shared/state/useAppState";

import {
  useGetCategoriesForResource,
  useGetRolesForResource,
} from "data/resources/resource";
import CPListBox, {
  CPBoxOption,
} from "shared/components/shared/CPListBox/CPListBox";
import {
  DownloadResource,
  useUpsertDownloadMutation,
  useDeleteDownloadMutation,
} from "data/resources/download";
import { ResourceCategory, ResourceRole } from "data/resources/resource";
import CPSpacer from "shared/components/shared/CPSpacer/CPSpacer";
import { useConfirm } from "shared/components/shared/CPConfirmModal/useCPConfirmModalContext";

export class UpsertDownloadRecord implements Omit<
  DownloadResource,
  "agencies"
> {
  id: number = -1;
  title: string = "";
  categories: string[] = [];
  roles: string[] = [];
  summary?: string = "";
  resourceTier: string = "";
  isActive: boolean = true;
  storageProvider?: string = "LOCAL";
  storageKey?: string = "";
  fileName?: string = "";
  mimeType?: string = "";
  downloadURL?: string = "";
}

interface DownloadDetailsModalProps {
  selectedDownload: UpsertDownloadRecord | null;
  onClose: () => void;
  onSave: () => void;
  onDelete?: () => void;
}

const DownloadDetailsModal: React.FC<DownloadDetailsModalProps> = ({
  selectedDownload,
  onClose,
  onSave,
  onDelete,
}) => {
  const { setToastMsg, setIsLoading } = useAppState();
  const confirm = useConfirm();
  const { data: categories, isLoading: categoriesLoading } =
    useGetCategoriesForResource(selectedDownload?.id ?? -1);
  const { data: roles, isLoading: rolesLoading } = useGetRolesForResource(
    selectedDownload?.id ?? -1,
  );
  const {
    mutate,
    isLoading: isSaving,
    error: saveError,
  } = useUpsertDownloadMutation();
  const { mutate: deleteDownload, isLoading: isDeleting } =
    useDeleteDownloadMutation();
  const [editDownload, setEditDownload] =
    React.useState<UpsertDownloadRecord | null>({
      ...selectedDownload,
    } as UpsertDownloadRecord);

  const [categoryOptions, setCategoryOptions] = React.useState<CPBoxOption>({
    available: [],
    selected: [],
  });

  const [roleOptions, setRoleOptions] = React.useState<CPBoxOption>({
    available: [],
    selected: [],
  });

  const [showSecurityHelp, setShowSecurityHelp] = React.useState(false);
  const [showFileConfigHelp, setShowFileConfigHelp] = React.useState(false);

  React.useEffect(() => {
    setIsLoading(categoriesLoading || rolesLoading || isSaving);
  }, [categoriesLoading, rolesLoading, isSaving, setIsLoading]);

  React.useEffect(() => {
    if (categories) {
      setCategoryOptions({
        available: categories.available.map((c: ResourceCategory) => ({
          value: c.name,
          label: c.parentName ? `${c.name} (${c.parentName})` : c.name,
          selected: false,
        })),
        selected: categories.assigned.map((c: ResourceCategory) => ({
          value: c.name,
          label: c.parentName ? `${c.name} (${c.parentName})` : c.name,
          selected: false,
        })),
      });
    }
  }, [categories]);

  React.useEffect(() => {
    if (roles) {
      setRoleOptions({
        available: roles.available.map((r: ResourceRole) => ({
          value: r.name,
          label: r.name,
          selected: false,
        })),
        selected: roles.assigned.map((r: ResourceRole) => ({
          value: r.name,
          label: r.name,
          selected: false,
        })),
      });
    }
  }, [roles]);

  // When a child category is selected, move the parent as well.
  const handleCategoryChange = React.useCallback(
    (options: CPBoxOption) => {
      if (!categories) {
        setCategoryOptions(options);
        return;
      }

      const allCategories = [...categories.available, ...categories.assigned];

      const getAncestorIds = (name: string): string[] => {
        const cat = allCategories.find((c) => c.name === name);
        if (!cat?.parentName) return [];
        const parent = allCategories.find((c) => c.name === cat.parentName);
        if (!parent) return [];
        return [parent.name, ...getAncestorIds(parent.name)];
      };

      const selectedIds = new Set(options.selected.map((o) => o.value));
      const ancestorIds = new Set(
        options.selected.flatMap((o) => getAncestorIds(o.value as string)),
      );

      const autoPromoted = options.available
        .filter(
          (o) =>
            ancestorIds.has(o.value as string) &&
            !selectedIds.has(o.value as string),
        )
        .map((o) => ({ ...o, selected: false }));

      const allRequiredIds = new Set(
        Array.from(selectedIds).concat(Array.from(ancestorIds)),
      );

      setCategoryOptions({
        available: options.available.filter(
          (o) => !allRequiredIds.has(o.value as string),
        ),
        selected: [...options.selected, ...autoPromoted],
      });
    },
    [categories],
  );

  const handleSave = () => {
    if (!editDownload) return;
    mutate(
      {
        ...editDownload,
        categories: categoryOptions.selected.map((c) => c.value as string),
        roles: roleOptions.selected.map((r) => r.value as string),
      },
      {
        onSuccess: () => {
          setToastMsg("Download saved successfully", "success");
          onSave();
        },
        onError: (error: unknown) => {
          if ((error as Record<string, unknown>)["errors"]) {
            setToastMsg(
              "Please check the form for errors and try again",
              "error",
            );
            return;
          }

          setToastMsg("An error occurred during the request.", "error");
        },
      },
    );
  };

  const handleDelete = () => {
    if (!editDownload) return;
    confirm((confirmed) => {
      if (confirmed) {
        deleteDownload(editDownload.id, {
          onSuccess: () => {
            setToastMsg("Download deleted successfully", "success");
            onClose();
            onDelete?.();
          },
          onError: () => {
            setToastMsg("Failed to delete download", "error");
          },
        });
      }
    }, `Are you sure you want to delete "${editDownload.title}"?`);
  };

  const isUrlProvider = editDownload?.storageProvider === "URL";

  const isNew = selectedDownload?.id === -1;

  return (
    <CPActionsheetModal
      title={isNew ? "Add Download" : "Edit Download"}
      open={selectedDownload !== null && !rolesLoading && !categoriesLoading}
      onClose={onClose}
      position="right"
      width={550}
      actionBtns={
        <Box spacing={8}>
          {!isNew && (
            <CPButton
              themeColor="error"
              fillMode="outline"
              onClick={handleDelete}
              disabled={isDeleting}
            >
              Delete
            </CPButton>
          )}
          <CPButton
            themeColor="primary"
            onClick={handleSave}
            disabled={isDeleting}
          >
            Save
          </CPButton>
        </Box>
      }
    >
      {selectedDownload && (
        <Box spacing={24} flexDirection="column" style={{ paddingBottom: 16 }}>
          <Box spacing={12} flexDirection="column">
            <Box className="cp-actionsheet-input-box">
              <CPTextBox
                toCapital
                required
                name="title"
                label="Download Title"
                value={editDownload?.title}
                error={saveError}
                onChange={(e) => {
                  setEditDownload(
                    (prev) =>
                      ({
                        ...prev,
                        title: e.target.value,
                      }) as UpsertDownloadRecord,
                  );
                }}
              />
            </Box>
            <Box className="cp-actionsheet-input-box">
              <CPCheckbox
                name="isActive"
                label="Active"
                checked={editDownload?.isActive}
                onChange={(e) => {
                  setEditDownload(
                    (prev) =>
                      ({
                        ...prev,
                        isActive: e.value,
                      }) as UpsertDownloadRecord,
                  );
                }}
              />
              <CPText>Show or hide this download link</CPText>
            </Box>

            <Box className="cp-actionsheet-input-box">
              <CPTextArea
                name="summary"
                label="Download Summary"
                error={saveError}
                disableResize
                value={editDownload?.summary}
                rows={4}
                onChange={(e) => {
                  setEditDownload(
                    (prev) =>
                      ({
                        ...prev,
                        summary: e.value,
                      }) as UpsertDownloadRecord,
                  );
                }}
              />
            </Box>
          </Box>

          <CPHrBreak spacing={5} />

          <Box
            spacing={12}
            flexDirection="column"
            className="cp-actionsheet-input-box"
          >
            <Box
              flexDirection="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Box flexDirection="column" spacing={4}>
                <CPText
                  textStyle="subtitle"
                  style={{
                    fontSize: 14,
                    fontWeight: 600,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <i className="fa-solid fa-file" style={{ marginRight: 8 }} />
                  File Configuration
                </CPText>
                <CPText style={{ opacity: 0.7 }}>
                  Storage location and file details
                </CPText>
              </Box>
              <CPHelpButton
                onClick={() => setShowFileConfigHelp(true)}
                title="File configuration help"
                aria-label="File configuration help"
              />
            </Box>
            <CPDropDownList
              name="storageProvider"
              label="Storage Provider"
              textField="label"
              dataItemKey="value"
              valueKey="value"
              defaultItem={{ label: "Please choose...", value: null }}
              data={[
                { label: "Azure Blob", value: "AZURE_BLOB" },
                { label: "SharePoint", value: "SHAREPOINT" },
                { label: "Local", value: "LOCAL" },
                { label: "URL", value: "URL" },
              ]}
              value={editDownload?.storageProvider ?? null}
              onChange={(e) => {
                setEditDownload(
                  (prev) =>
                    ({
                      ...prev,
                      storageProvider: e.value,
                    }) as UpsertDownloadRecord,
                );
              }}
            />

            {isUrlProvider && (
              <CPTextBox
                required
                name="downloadURL"
                label="Download URL"
                value={editDownload?.downloadURL}
                error={saveError}
                onChange={(e) => {
                  setEditDownload(
                    (prev) =>
                      ({
                        ...prev,
                        downloadURL: e.target.value,
                      }) as UpsertDownloadRecord,
                  );
                }}
              />
            )}

            {!isUrlProvider && (
              <>
                <CPTextBox
                  name="storageKey"
                  label="Storage Key"
                  value={editDownload?.storageKey}
                  error={saveError}
                  onChange={(e) => {
                    setEditDownload(
                      (prev) =>
                        ({
                          ...prev,
                          storageKey: e.target.value,
                        }) as UpsertDownloadRecord,
                    );
                  }}
                />

                <CPTextBox
                  required
                  name="fileName"
                  label="File Name"
                  value={editDownload?.fileName}
                  error={saveError}
                  onChange={(e) => {
                    setEditDownload(
                      (prev) =>
                        ({
                          ...prev,
                          fileName: e.target.value,
                        }) as UpsertDownloadRecord,
                    );
                  }}
                />

                <CPTextBox
                  name="mimeType"
                  label="MIME Type"
                  value={editDownload?.mimeType}
                  error={saveError}
                  onChange={(e) => {
                    setEditDownload(
                      (prev) =>
                        ({
                          ...prev,
                          mimeType: e.target.value,
                        }) as UpsertDownloadRecord,
                    );
                  }}
                />
              </>
            )}
          </Box>

          <Box spacing={12} flexDirection="column">
            <Box className="cp-actionsheet-input-box">
              <Box
                flexDirection="row"
                alignItems="center"
                justifyContent="flex-start"
                spacing={10}
              >
                <Box flexDirection="column" spacing={4}>
                  <CPText
                    textStyle="h6"
                    style={{
                      fontSize: 14,
                      fontWeight: 600,
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <i
                      className="fa-solid fa-lock"
                      style={{ marginRight: 8 }}
                    />
                    Access & Security
                  </CPText>
                  <CPText style={{ opacity: 0.7 }}>
                    Control who can access this resource
                  </CPText>
                </Box>
                <CPHelpButton
                  onClick={() => setShowSecurityHelp(true)}
                  title="How does security work?"
                  aria-label="Visibility help"
                />
              </Box>
              <CPSpacer height={15} />
              <Box>
                <CPText textStyle="subtitle" style={{ fontSize: 13 }}>
                  Resource Tier
                </CPText>
                <CPText
                  textStyle="caption"
                  style={{ opacity: 0.8, fontSize: 12, marginTop: 4 }}
                >
                  Lock the resource to agencies with matching tier access.
                </CPText>
              </Box>

              <CPSpacer height={8} />

              <CPDropDownList
                name="resourceTier"
                label="Tier"
                textField="label"
                dataItemKey="value"
                valueKey="value"
                defaultItem={{ label: "Please choose...", value: null }}
                data={[
                  { label: "Bronze", value: "BRONZE" },
                  { label: "Silver", value: "SILVER" },
                  { label: "Gold", value: "GOLD" },
                ]}
                value={editDownload?.resourceTier ?? null}
                onChange={(e) => {
                  setEditDownload(
                    (prev) =>
                      ({
                        ...prev,
                        resourceTier: e.value,
                      }) as UpsertDownloadRecord,
                  );
                }}
              />
            </Box>

            <CPSpacer height={8} />
            <Box className="cp-actionsheet-input-box">
              <CPListBox
                name="categories"
                headerText="Assigned Categories *"
                filterable
                boxOptions={categoryOptions}
                onChange={handleCategoryChange}
                error={saveError}
              />
            </Box>

            <CPSpacer height={8} />
            <Box className="cp-actionsheet-input-box">
              {" "}
              <CPListBox
                name="roles"
                headerText="Assigned Roles"
                filterable
                boxOptions={roleOptions}
                onChange={(options) => setRoleOptions(options)}
              />
            </Box>
          </Box>
        </Box>
      )}

      <CPResourceSecurityHelpModal
        open={showSecurityHelp}
        onClose={() => setShowSecurityHelp(false)}
      />
      <FileStorageConfigHelpModal
        open={showFileConfigHelp}
        onClose={() => setShowFileConfigHelp(false)}
      />
    </CPActionsheetModal>
  );
};

export default DownloadDetailsModal;
